import React, { useState } from 'react';

export default function CompetitorAnalysis({ user, onCreditsUpdate }) {
  const [channelsInput, setChannelsInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResults(null);

    try {
      // Clean and process user input
      const rawInputs = channelsInput.split(',').map(c => c.trim()).filter(c => c);
      
      if (rawInputs.length < 2) {
        throw new Error("Please enter at least 2 channels or video URLs separated by a comma.");
      }
      if (rawInputs.length > 4) {
        throw new Error("You can only compare up to 4 channels at a time.");
      }

      // Send the cleaned array directly to the backend
      const response = await fetch('https://tube.contentserp.in/api/analysis/competitor-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ channels: rawInputs })
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.error || responseData.message || 'Failed to analyze competitors. Make sure the channels exist.');
      }

      const finalData = responseData.data ? responseData.data : responseData;
      setResults(finalData);
      
      // FIXED LINES:
      if (responseData.remainingCredits !== undefined) {
        onCreditsUpdate(responseData.remainingCredits);
      } else if (finalData.remainingCredits !== undefined) {
        onCreditsUpdate(finalData.remainingCredits);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="analysis-container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', color: '#333', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Header Section */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '28px', margin: '0 0 10px 0', color: '#111', display: 'flex', alignItems: 'center', gap: '10px' }}>
          🏆 Competitor Analysis
        </h2>
        <p style={{ color: '#666', margin: 0, fontSize: '15px' }}>
          Enter 2 to 4 competitors to compare their performance. You can use <strong>Channel Names</strong>, <strong>@handles</strong>, or <strong>Video Links</strong> (separated by commas).
        </p>
      </div>

      {/* Input Form */}
      <form onSubmit={handleAnalyze} style={{ display: 'flex', gap: '15px', marginBottom: '30px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 300px', position: 'relative' }}>
          <input 
            type="text" 
            value={channelsInput}
            onChange={(e) => setChannelsInput(e.target.value)}
            placeholder="e.g. Marques Brownlee, @MrBeast, https://youtube.com/watch?v=..."
            style={{ 
              width: '100%', 
              padding: '16px 20px', 
              borderRadius: '10px', 
              border: '1px solid #d1d5db', 
              background: '#fff', 
              color: '#333',
              fontSize: '15px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            required 
          />
        </div>
        <button 
          type="submit" 
          disabled={loading}
          style={{ 
            padding: '16px 30px', 
            borderRadius: '10px', 
            background: loading ? '#9ca3af' : '#ef4444', 
            color: 'white', 
            border: 'none', 
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '15px',
            minWidth: '200px',
            transition: 'background 0.2s',
            boxShadow: '0 4px 6px rgba(239, 68, 68, 0.2)'
          }}
        >
          {loading ? '⏳ Analyzing...' : 'Analyze (Costs Credits)'}
        </button>
      </form>

      {/* Error Message */}
      {error && (
        <div style={{ background: '#fef2f2', borderLeft: '4px solid #ef4444', color: '#991b1b', padding: '16px', borderRadius: '8px', marginBottom: '30px', fontSize: '15px' }}>
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      {/* Results Section */}
      {results && results.channels && (
        <div style={{ animation: 'fadeIn 0.5s ease-in' }}>
          
          {/* AI Insights Banner */}
          {results.insights && (
            <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', marginBottom: '30px', borderLeft: '4px solid #3b82f6', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #f3f4f6' }}>
              <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#1f2937', fontSize: '18px' }}>
                💡 AI Analysis Report
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
                {results.insights.map((insight, idx) => (
                  <div key={idx} style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <span style={{ color: '#3b82f6', fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
                      {insight.type.replace('_', ' ')}
                    </span>
                    <span style={{ color: '#4b5563', fontSize: '14px', lineHeight: '1.5' }}>
                      {insight.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Channel Comparison Cards */}
          <div style={{ display: 'flex', gap: '20px', overflowX: 'auto', paddingBottom: '15px' }}>
            {results.channels.map((channel, index) => (
              <div key={index} style={{ background: '#fff', borderRadius: '16px', padding: '24px', minWidth: '320px', flex: 1, boxShadow: '0 4px 15px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
                
                {/* Channel Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
                  <img src={channel.thumbnails?.high?.url || channel.thumbnails?.default?.url} alt={channel.title} style={{ width: '70px', height: '70px', borderRadius: '50%', border: '2px solid #e5e7eb', objectFit: 'cover' }} />
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>
                      {channel.title}
                    </h3>
                    <a href={`https://youtube.com/${channel.customUrl !== 'N/A' ? '@'+channel.customUrl : 'channel/'+channel.channelId}`} target="_blank" rel="noreferrer" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '14px' }}>
                      {channel.customUrl !== 'N/A' ? '@'+channel.customUrl : 'Visit Channel'} ↗
                    </a>
                  </div>
                </div>
                
                {/* Metrics Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '25px' }}>
                  <div style={{ background: '#f9fafb', padding: '15px 10px', borderRadius: '10px', textAlign: 'center', border: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Subscribers</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#1f2937' }}>{formatNumber(channel.subscriberCount)}</div>
                  </div>
                  <div style={{ background: '#f9fafb', padding: '15px 10px', borderRadius: '10px', textAlign: 'center', border: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Total Views</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#1f2937' }}>{formatNumber(channel.viewCount)}</div>
                  </div>
                  <div style={{ background: '#f9fafb', padding: '15px 10px', borderRadius: '10px', textAlign: 'center', border: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Videos/Month</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#1f2937' }}>{channel.analytics?.videosPerMonth || '0'}</div>
                  </div>
                  <div style={{ background: '#eff6ff', padding: '15px 10px', borderRadius: '10px', textAlign: 'center', border: '1px solid #bfdbfe' }}>
                    <div style={{ fontSize: '12px', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Engagement</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#1d4ed8' }}>{channel.analytics?.engagementRate || 0}%</div>
                  </div>
                </div>

                {/* Recent Videos List */}
                <h4 style={{ fontSize: '14px', margin: '0 0 12px 0', color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recent Videos</h4>
                <div style={{ maxHeight: '240px', overflowY: 'auto', paddingRight: '8px' }}>
                  {channel.recentVideos && channel.recentVideos.length > 0 ? channel.recentVideos.slice(0, 4).map((vid, vIdx) => (
                    <div key={vIdx} style={{ marginBottom: '10px', background: '#f9fafb', padding: '12px', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
                      <a href={`https://youtube.com/watch?v=${vid.videoId}`} target="_blank" rel="noreferrer" style={{ fontWeight: '600', color: '#111', textDecoration: 'none', display: 'block', marginBottom: '6px', fontSize: '13px', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {vid.title}
                      </a>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280', fontSize: '12px', fontWeight: '500' }}>
                        <span>👁️ {formatNumber(vid.views)}</span>
                        <span>👍 {formatNumber(vid.likes)}</span>
                      </div>
                    </div>
                  )) : (
                    <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No recent videos found.</div>
                  )}
                </div>

              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

